import type { JSX } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CreateStudentDialog } from '@/features/students/create-student-dialog';
import { useStudents } from '@/features/students/hooks';
import { useHasCapability } from '@/lib/capabilities';

const PAGE_SIZE = 20;

/**
 * The real Students list (Phase G-1, Job 6). `StudentView` now carries the
 * account holder's `fullName` (G-9, Job 9) - the primary label. The raw
 * `id` remains visible underneath (a genuinely useful technical
 * identifier - supports/debugging, and the same value used in the link
 * target below), just no longer the only thing an admin sees. The backend
 * narrows this same list to Self/Child automatically for a
 * non-broad-access caller (`authz/student-scope.ts`) - this page renders
 * exactly what it returns, for every role, with no client-side role
 * branching.
 */
export function StudentsPage(): JSX.Element {
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const { data, isLoading, isError } = useStudents(page, PAGE_SIZE);
  const canCreate = useHasCapability('students:manage');

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Santri</h1>
          <p className="text-sm text-muted-foreground">Daftar santri di organisasi ini.</p>
        </div>
        {canCreate && (
          <Button
            type="button"
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            Tambah Santri
          </Button>
        )}
      </div>

      {isLoading && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}

      {isError && (
        <Alert variant="destructive">
          <AlertDescription>Gagal memuat daftar santri.</AlertDescription>
        </Alert>
      )}

      {!isLoading && !isError && data !== undefined && data.items.length === 0 && (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">Belum ada santri.</CardContent>
        </Card>
      )}

      {!isLoading && !isError && data !== undefined && data.items.length > 0 && (
        <>
          <ul className="flex flex-col gap-2">
            {data.items.map((student) => (
              <li key={student.id}>
                <Link to={`/students/${student.id}`}>
                  <Card className="transition-colors hover:bg-secondary/50">
                    <CardContent className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate text-sm font-medium">{student.fullName}</span>
                        <span className="truncate font-mono text-xs text-muted-foreground">
                          {student.email}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {student.guardianName !== null && (
                          <span className="text-sm text-muted-foreground">
                            Wali: {student.guardianName}
                          </span>
                        )}
                        <Badge variant="secondary" className="capitalize">
                          {student.enrollmentStatus.toLowerCase()}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </li>
            ))}
          </ul>

          {data.total_pages > 1 && (
            <div className="flex items-center justify-between gap-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => {
                  setPage((current) => Math.max(1, current - 1));
                }}
              >
                Sebelumnya
              </Button>
              <span className="text-sm text-muted-foreground">
                Halaman {data.page} dari {data.total_pages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= data.total_pages}
                onClick={() => {
                  setPage((current) => Math.min(data.total_pages, current + 1));
                }}
              >
                Berikutnya
              </Button>
            </div>
          )}
        </>
      )}

      <CreateStudentDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => {
          setPage(1);
        }}
      />
    </div>
  );
}
